import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTexts } from '../hooks/useTexts';
import { publicApiService } from '../services/publicApi';

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  
  // Hook pour les textes dynamiques
  const { getText } = useTexts();
  
  const [formData, setFormData] = useState({
    nom: '',
    prenoms: '',
    email: '',
    telephone: '',
    sujet: 'INFORMATION',
    message: '',
    projet_interesse: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Récupérer les paramètres de projet depuis l'URL
  useEffect(() => {
    const projetId = searchParams.get('projet');
    const projetNom = searchParams.get('projet_nom');
    const typeMaison = searchParams.get('type_maison');
    
    if (projetId && projetNom) {
      let projetInteresse = `Projet: ${decodeURIComponent(projetNom)} (ID: ${projetId})`;
      if (typeMaison) {
        projetInteresse += ` - Type de bien: ${decodeURIComponent(typeMaison)}`;
      }
      
      setFormData(prev => ({
        ...prev,
        projet_interesse: projetInteresse,
        sujet: 'INFORMATION_PROJET'
      }));
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Préparer les données du formulaire
      const submitData = {
        nom: formData.nom,
        prenoms: formData.prenoms,
        email: formData.email,
        telephone: formData.telephone,
        sujet: formData.sujet,
        message: formData.projet_interesse 
          ? `${formData.message}\n\nProjet d'intérêt: ${formData.projet_interesse}`
          : formData.message,
        projet_interesse: formData.projet_interesse
      };
      
      // Envoyer le message via l'API
      await publicApiService.sendContactMessage(submitData);
      
      // Afficher le message de succès avec toastr
      toast.success('Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Réinitialiser le formulaire
      setFormData({
        nom: '',
        prenoms: '',
        email: '',
        telephone: '',
        sujet: 'INFORMATION',
        message: '',
        projet_interesse: ''
      });
      
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi du message:', error);
      
      // Afficher le message d'erreur avec toastr
      toast.error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Erreur lors de l\'envoi du message. Veuillez réessayer.',
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Données de contact dynamiques
  const contactInfo = [
    {
      icon: MapPin,
      title: getText('contact.info.address'),
      details: [
        getText('contact.info.address_value'),
        getText('contact.info.city_value')
      ]
    },
    {
      icon: Phone,
      title: getText('contact.info.phone'),
      details: [
        getText('contact.info.phone_value'),
        getText('contact.info.phone_value_2')
      ]
    },
    {
      icon: Mail,
      title: getText('contact.info.email'),
      details: [
        getText('contact.info.email_value'),
        getText('contact.info.email_value_2')
      ]
    },
    {
      icon: Clock,
      title: getText('contact.info.hours'),
      details: [
        getText('contact.info.hours_value'),
        getText('contact.info.hours_value_2')
      ]
    }
  ];

  const offices = [
    {
      name: getText('contact.office.headquarters.name'),
      address: getText('contact.office.headquarters.address'),
      phone: getText('contact.office.headquarters.phone'),
      hours: getText('contact.office.headquarters.hours')
    },
    {
      name: getText('contact.office.cocody.name'),
      address: getText('contact.office.cocody.address'),
      phone: getText('contact.office.cocody.phone'),
      hours: getText('contact.office.cocody.hours')
    },
    {
      name: getText('contact.office.songon.name'),
      address: getText('contact.office.songon.address'),
      phone: getText('contact.office.songon.phone'),
      hours: getText('contact.office.songon.hours')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {getText('contact.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl">
            {getText('contact.hero.description')}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {getText('contact.form.title')}
                </h2>
                <p className="text-gray-600">
                  {getText('contact.form.description')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                      {getText('contact.form.name')} *
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={getText('placeholder.name')}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="prenoms" className="block text-sm font-medium text-gray-700 mb-2">
                      {getText('contact.form.firstname')} *
                    </label>
                    <input
                      type="text"
                      id="prenoms"
                      name="prenoms"
                      value={formData.prenoms}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={getText('placeholder.firstname')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {getText('contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={getText('placeholder.email')}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                      {getText('contact.form.phone')}
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={getText('placeholder.phone')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="sujet" className="block text-sm font-medium text-gray-700 mb-2">
                    {getText('contact.form.subject')} *
                  </label>
                  <select
                    id="sujet"
                    name="sujet"
                    value={formData.sujet}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="INFORMATION">{getText('contact.subject.information')}</option>
                    <option value="INFORMATION_PROJET">{getText('contact.subject.project_info')}</option>
                    <option value="PROJET">{getText('contact.subject.project_question')}</option>
                    <option value="FINANCEMENT">{getText('contact.subject.financing')}</option>
                    <option value="VISITE">{getText('contact.subject.visit')}</option>
                    <option value="SUPPORT">{getText('contact.subject.support')}</option>
                    <option value="AUTRE">{getText('contact.subject.other')}</option>
                  </select>
                </div>

                {formData.projet_interesse && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Projet d'intérêt
                    </label>
                    <div className="w-full px-4 py-2 border border-blue-200 rounded-lg bg-blue-50 text-blue-800">
                      {formData.projet_interesse}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {getText('contact.form.message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder={getText('placeholder.message')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {getText('contact.form.sending')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {getText('contact.form.send')}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            {/* Contact Details */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {getText('contact.info.title')}
              </h3>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <info.icon className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                        {info.title}
                      </h4>
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-sm text-gray-600">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

  
            {/* Office Hours */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {getText('contact.offices.title')}
              </h3>
              <div className="space-y-4">
                {offices.map((office, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {office.name}
                    </h4>
                    <p className="text-sm text-gray-600 mb-1">
                      {office.address}
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      Tel: {office.phone}
                    </p>
                    <p className="text-sm text-blue-600">
                      {office.hours}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}